import type {
  WhatsAppCampaignStatus,
  WhatsAppCategory,
  WhatsAppHeader,
  WhatsAppMessagePurpose,
  WhatsAppMessageStatus,
  WhatsAppMode,
  WhatsAppStatementStatus,
} from "custom-types";
import { FEATURE_DISABLED_MESSAGE } from "./features";
import { formatPaisa, nprToPaisa } from "./npr";

/*
 * WhatsApp messaging (claude/design/whatsapp-messaging/): the pure pieces the
 * control panel, SageMIN and the customer site share. Names and wording for
 * the API's enums, the error copy for its codes (02 §11 "Errors"), template
 * placeholder counting, the NPR rate rule and the customer number rule.
 * Nothing here formats money: amounts go through `formatNpr` (./npr).
 */

/* ---------------------------------------------------------------- labels */

export const WHATSAPP_PURPOSE_LABELS: Readonly<
  Record<WhatsAppMessagePurpose, string>
> = {
  ticket: "Ticket",
  ticket_food: "Ticket and food",
  food: "Food order",
  reservation: "Reservation",
  otp: "Verification code",
  campaign: "Promotion",
  test: "Test",
};

export const WHATSAPP_CATEGORY_LABELS: Readonly<
  Record<WhatsAppCategory, string>
> = {
  utility: "Utility",
  authentication: "Authentication",
  marketing: "Marketing",
};

export const WHATSAPP_MODE_LABELS: Readonly<Record<WhatsAppMode, string>> = {
  shared: "Shared (Picovico)",
  own: "Own number",
};

export const WHATSAPP_HEADER_LABELS: Readonly<Record<WhatsAppHeader, string>> =
  {
    none: "None",
    image: "Image",
    document: "Document",
  };

export const WHATSAPP_STATUS_LABELS: Readonly<
  Record<WhatsAppMessageStatus, string>
> = {
  queued: "Queued",
  sending: "Sending",
  accepted: "Accepted",
  sent: "Sent",
  delivered: "Delivered",
  read: "Read",
  failed: "Failed",
  rejected: "Rejected",
  skipped: "Skipped",
  unconfirmed: "Unconfirmed",
};

export const WHATSAPP_STATEMENT_STATUS_LABELS: Readonly<
  Record<WhatsAppStatementStatus, string>
> = {
  draft: "Draft",
  final: "Final",
  invoiced: "Invoiced",
  paid: "Paid",
};

export const WHATSAPP_CAMPAIGN_STATUS_LABELS: Readonly<
  Record<WhatsAppCampaignStatus, string>
> = {
  draft: "Draft",
  scheduled: "Scheduled",
  sending: "Sending",
  sent: "Sent",
  cancelled: "Cancelled",
  failed: "Failed",
};

/* ---------------------------------------------------------- usage tables */

/**
 * The status counts a usage "by purpose" row carries, in reading order. They
 * are amphitheater's `whatsapp/usage.py` `STATUS_COLUMNS` (`sending` is
 * counted as `queued`), so together they add up to every message of the row.
 */
export const WHATSAPP_USAGE_STATUS_COLUMNS = [
  "queued",
  "accepted",
  "sent",
  "delivered",
  "read",
  "failed",
  "rejected",
  "skipped",
  "unconfirmed",
] as const satisfies readonly WhatsAppMessageStatus[];

export type WhatsAppUsageStatusColumn =
  (typeof WHATSAPP_USAGE_STATUS_COLUMNS)[number];

type WhatsAppUsageCounts = Partial<
  Record<WhatsAppUsageStatusColumn, number | null | undefined>
>;

/**
 * The status columns worth showing for a table of rows: those with a count
 * above zero in at least one row, in `WHATSAPP_USAGE_STATUS_COLUMNS` order.
 * All-zero columns are dropped so the table stays readable.
 */
export function whatsappUsageStatusColumns(
  rows: readonly WhatsAppUsageCounts[],
): WhatsAppUsageStatusColumn[] {
  return WHATSAPP_USAGE_STATUS_COLUMNS.filter((column) =>
    rows.some((row) => Number(row[column] ?? 0) > 0),
  );
}

/** Every message of a usage row: the sum of its status counts. */
export function whatsappUsageRowTotal(row: WhatsAppUsageCounts): number {
  return WHATSAPP_USAGE_STATUS_COLUMNS.reduce(
    (sum, column) => sum + Number(row[column] ?? 0),
    0,
  );
}

/* ---------------------------------------------------------------- modes */

/**
 * The number WhatsApp really sends from, as amphitheater resolves it
 * (`whatsapp/resolve.py`): the chosen mode when it is still allowed; with
 * none chosen, Picovico's shared number whenever `shared` is allowed (and
 * billed); else nothing. So `active_mode: null` with `shared` allowed is
 * **sending**, on the shared number. Null means nothing is sent.
 */
export function effectiveWhatsappMode({
  active_mode,
  allowed_modes,
}: {
  active_mode: WhatsAppMode | null | undefined;
  allowed_modes: readonly WhatsAppMode[] | null | undefined;
}): WhatsAppMode | null {
  const allowed = allowed_modes ?? [];
  if (active_mode && allowed.includes(active_mode)) return active_mode;
  return allowed.includes("shared") ? "shared" : null;
}

/** A label for any enum value, falling back to the value itself. */
export function whatsappLabel<K extends string>(
  labels: Readonly<Record<K, string>>,
  value: string | null | undefined,
): string {
  if (!value) return "—";
  return (labels as Record<string, string>)[value] ?? value;
}

/* ----------------------------------------------------------- placeholders */

const PLACEHOLDER = /\{\{\s*(\d+)\s*\}\}/g;

/** Meta allows at most this many placeholders in one template: `{{1}}` to `{{9}}`. */
export const WHATSAPP_MAX_TEMPLATE_PLACEHOLDERS = 9;

/** The API's 400 `TEMPLATE_INVALID` message when a template goes over the limit. */
export const WHATSAPP_PLACEHOLDER_LIMIT_MESSAGE = `WhatsApp (Meta) allows at most ${WHATSAPP_MAX_TEMPLATE_PLACEHOLDERS} placeholders in a template: {{1}} to {{${WHATSAPP_MAX_TEMPLATE_PLACEHOLDERS}}}.`;

/**
 * The placeholder numbers in a template body, sorted and without repeats:
 * "Hi {{2}}, your seats {{1}} ({{2}})" → [1, 2].
 */
export function templatePlaceholders(body: string | null | undefined): number[] {
  if (!body) return [];
  const found = new Set<number>();
  for (const match of body.matchAll(PLACEHOLDER)) found.add(Number(match[1]));
  return Array.from(found).sort((a, b) => a - b);
}

/**
 * How many parameters a template body needs: one per placeholder, numbered
 * `{{1}}` to `{{n}}` (Meta numbers them in order without gaps). The server
 * refuses a template whose `params` do not match (400 `TEMPLATE_INVALID`).
 */
export function countTemplatePlaceholders(
  body: string | null | undefined,
): number {
  return templatePlaceholders(body).length;
}

/**
 * The size of a body's placeholders for the "n / 9" counter: how many there are, or the
 * highest number when that is larger (`{{12}}` alone reads 12, as the API refuses it).
 */
export function templatePlaceholderExtent(body: string | null | undefined): number {
  const numbers = templatePlaceholders(body);
  return Math.max(numbers.length, numbers[numbers.length - 1] ?? 0);
}

/**
 * What is wrong with a body's placeholders, or null: at most nine, and they
 * must run `{{1}}`, `{{2}}`… with none missing.
 */
export function templatePlaceholderProblem(
  body: string | null | undefined,
): string | null {
  const numbers = templatePlaceholders(body);
  // Over the cap by count, or by number: {{1}}…{{8}} {{10}} has nine, but {{10}} is out.
  if (numbers.length > WHATSAPP_MAX_TEMPLATE_PLACEHOLDERS || numbers[numbers.length - 1] > WHATSAPP_MAX_TEMPLATE_PLACEHOLDERS) {
    return WHATSAPP_PLACEHOLDER_LIMIT_MESSAGE;
  }
  if (numbers[0] === 0) return "Placeholders start at {{1}}. Remove {{0}}.";
  const gap = numbers.findIndex((number, index) => number !== index + 1);
  if (gap === -1) return null;
  const expected = gap + 1;
  return `Placeholders must be numbered {{1}}, {{2}}… in order. {{${expected}}} is missing.`;
}

/**
 * `params` resized to `count`: values already chosen stay in place, new
 * slots are empty and extra ones are dropped.
 */
export function fitTemplateParams(params: string[], count: number): string[] {
  return Array.from({ length: Math.max(0, count) }, (_, index) =>
    String(params[index] ?? ""),
  );
}

/** The body with `{{n}}` replaced by `values[n - 1]` (left as is when missing). */
export function fillTemplatePreview(
  body: string | null | undefined,
  values: string[],
): string {
  if (!body) return "";
  return body.replace(PLACEHOLDER, (whole, number: string) => {
    const value = values[Number(number) - 1];
    return value ? value : whole;
  });
}

/* ------------------------------------------------------------------ rates */

const NPR_RATE = /^\d+(\.\d{1,2})?$/;

/** The highest rate per message amphitheater takes: रु 1,000.00, in paisa. */
export const WHATSAPP_MAX_RATE_PAISA = 100_000;

/**
 * What is wrong with a rate typed in rupees per message, or null. The rule
 * is the server's: at most 2 decimals, from 0 to रु 1,000.00 (400 `INVALID`
 * otherwise). With `optional`, an empty field is fine (it means "use the
 * platform default").
 */
export function whatsappRateProblem(
  value: string | null | undefined,
  { optional = false }: { optional?: boolean } = {},
): string | null {
  const text = (value ?? "").trim();
  if (!text) return optional ? null : "Enter a rate, for example 1.50.";
  if (text.startsWith("-")) return "A rate can’t be negative.";
  if (!NPR_RATE.test(text)) {
    return "Use rupees with at most 2 decimals, for example 1.50.";
  }
  const paisa = nprToPaisa(text);
  if (paisa === null || paisa > BigInt(WHATSAPP_MAX_RATE_PAISA)) {
    return `A rate can be at most ${formatPaisa(BigInt(WHATSAPP_MAX_RATE_PAISA), { decimals: 2 })} per message.`;
  }
  return null;
}

/* ------------------------------------------------------------------- tags */

/** amphitheater's tag limits (contacts and a campaign's audience). */
export const WHATSAPP_MAX_TAGS = 20;
export const WHATSAPP_MAX_TAG_LENGTH = 64;

/**
 * What is wrong with a list of tags, or null: at most 20, each at most 64
 * characters (the server refuses more with 400 `INVALID`).
 */
export function whatsappTagsProblem(
  tags: readonly string[] | null | undefined,
): string | null {
  const list = (tags ?? []).map((tag) => tag.trim()).filter(Boolean);
  if (new Set(list).size > WHATSAPP_MAX_TAGS) {
    return `Use at most ${WHATSAPP_MAX_TAGS} tags.`;
  }
  const long = list.find((tag) => tag.length > WHATSAPP_MAX_TAG_LENGTH);
  if (long) {
    return `A tag can be at most ${WHATSAPP_MAX_TAG_LENGTH} characters (“${long.slice(0, 20)}…” is longer).`;
  }
  return null;
}

/* ------------------------------------------------------- customer numbers */

/**
 * A Nepali mobile number as the customer site accepts it for WhatsApp
 * (04-ui §3): optional +977 or 977, then 9 and nine more digits. Check it on
 * `cleanWhatsappNumber(value)`; the server normalises it to E.164.
 */
export const WHATSAPP_NUMBER_PATTERN = /^(\+?977)?9\d{9}$/;

/** The number without spaces and dashes, as it is checked and sent. */
export function cleanWhatsappNumber(value: string | null | undefined): string {
  return (value ?? "").replace(/[\s-]/g, "");
}

export function isValidWhatsappNumber(value: string | null | undefined): boolean {
  return WHATSAPP_NUMBER_PATTERN.test(cleanWhatsappNumber(value));
}

/**
 * The start of a number for a sentence such as "We sent a code on WhatsApp
 * to 98…": its first two national digits and an ellipsis.
 */
export function whatsappNumberPrefix(value: string | null | undefined): string {
  const national = cleanWhatsappNumber(value).replace(/^\+?977/, "");
  return national.length >= 2 ? `${national.slice(0, 2)}…` : "";
}

/* ----------------------------------------------------------------- errors */

/**
 * Plain words for every WhatsApp error code. People read these, so a code or
 * field name is never shown. Codes in `DETAIL_FIRST` show the server's own
 * sentence when it is plain (Sparrow's reason for refusing tokens, which
 * template key is unknown).
 */
export const WHATSAPP_ERROR_COPY: Readonly<Record<string, string>> =
  Object.freeze({
    FEATURE_DISABLED: FEATURE_DISABLED_MESSAGE,
    WHATSAPP_OWN_ACCOUNT_REQUIRED:
      "Promotions can only be sent from the company’s own WhatsApp number. Set it up in WhatsApp settings first.",
    INVALID: "Please check what you entered and try again.",
    SPARROW_LOGIN_FAILED:
      "Sparrow did not accept these tokens. Check that both were copied whole.",
    TEMPLATE_INVALID:
      "The template was not accepted. Check that every placeholder has a value and the purpose has no other active template.",
    CAMPAIGN_STATE: "This campaign can’t be changed any more.",
    ESTIMATE_CHANGED:
      "The number of recipients has changed. Check the new count, then send again.",
    OTP_THROTTLED: "Too many codes were asked for. Please wait and try again.",
    THROTTLED: "Too many changes. Please wait a little and try again.",
    WHATSAPP_NOT_CONFIGURED:
      "The server is not set up for WhatsApp yet. Please contact Picovico.",
    PERIOD_NOT_OVER:
      "This month can’t be finalized yet. Statements are finalized a few days after the month ends.",
    STATEMENT_STALE:
      "Usage changed after this draft was made. Make the drafts again, then finalize.",
    NOT_A_DRAFT: "This statement is no longer a draft. The list is up to date now.",
    NOT_FINAL: "Only a final statement can be marked. The list is up to date now.",
    NOT_FOUND: "This is no longer available.",
  });

/**
 * Shown when a credentials save made no webhook URL although the account had
 * none before: amphitheater makes the URL from `WHATSAPP_PUBLIC_BASE_URL`, and
 * without it answers `webhook_url: null`.
 */
export const WHATSAPP_NO_WEBHOOK_HINT =
  "No webhook URL was made: the server has no public address set. Ask an engineer to set WHATSAPP_PUBLIC_BASE_URL, then use Rotate.";

/**
 * Whether a credentials save should show `WHATSAPP_NO_WEBHOOK_HINT`: it
 * answered no URL, the account had no URL before (`hadWebhookUrl`) and no
 * delivery report ever came (an account removed and saved again keeps its
 * earlier URL, and its reports prove that URL works).
 */
export function whatsappWebhookUrlMissing({
  webhookUrl,
  hadWebhookUrl,
  lastWebhookAt,
}: {
  webhookUrl: string | null | undefined;
  hadWebhookUrl: boolean;
  lastWebhookAt: string | null | undefined;
}): boolean {
  return !webhookUrl && !hadWebhookUrl && !lastWebhookAt;
}

/** When no answer came back at all (server down, no connection). */
export const WHATSAPP_NETWORK_MESSAGE =
  "Could not reach the server. Check your connection and try again.";

/*
 * Codes whose `detail` is the useful part: Sparrow's reason, the template
 * problem, the field, and which server setting is missing (credential keys
 * or the public base URL) for WHATSAPP_NOT_CONFIGURED.
 */
const DETAIL_FIRST = new Set([
  "SPARROW_LOGIN_FAILED",
  "TEMPLATE_INVALID",
  "INVALID",
  "WHATSAPP_NOT_CONFIGURED",
]);

export interface WhatsAppErrorParts {
  code: string;
  detail?: string;
  /** `OTP_THROTTLED`, `THROTTLED`: seconds until another try is taken. */
  retryAfter?: number;
}

type ErrorLike = {
  code?: unknown;
  detail?: unknown;
  retry_after?: unknown;
  data?: unknown;
} | null;

/**
 * The `{code, detail}` of a refused WhatsApp request: the body an RTK Query
 * error carries, or an axios response holding it. Null when it is neither.
 */
export function whatsappErrorParts(error: unknown): WhatsAppErrorParts | null {
  const candidate = error as ErrorLike;
  if (!candidate || typeof candidate !== "object") return null;
  const body = (
    typeof candidate.code === "string" ? candidate : candidate.data ?? null
  ) as ErrorLike;
  if (!body || typeof body !== "object") return null;
  if (typeof body.code !== "string" || !body.code) return null;
  const retry = Number(body.retry_after);
  return {
    code: body.code,
    detail: typeof body.detail === "string" ? body.detail : undefined,
    retryAfter: Number.isFinite(retry) && retry > 0 ? retry : undefined,
  };
}

/** A server sentence fit to show as it is: short, no markup, no backticks. */
export function plainWhatsappDetail(detail: unknown): string | null {
  if (typeof detail !== "string") return null;
  const text = detail.replace(/`/g, "").trim();
  if (!text || text.length > 300 || /[<>]/.test(text)) return null;
  return text;
}

/** "Try again in 25 seconds" / "… 2 minutes" / "… 1 hour", for `retry_after`. */
export function whatsappTryAgainText(seconds: number): string {
  const unit = (count: number, one: string) =>
    `${count} ${one}${count === 1 ? "" : "s"}`;
  const whole = Math.max(1, Math.ceil(seconds));
  if (whole < 60) return `Try again in ${unit(whole, "second")}`;
  const minutes = Math.ceil(whole / 60);
  if (minutes < 60) return `Try again in ${unit(minutes, "minute")}`;
  return `Try again in ${unit(Math.ceil(minutes / 60), "hour")}`;
}

/** "Wait 25 seconds" / "Wait 2 minutes", for `retry_after`. */
export function whatsappRetryText(seconds: number): string {
  if (seconds < 60) {
    const whole = Math.max(1, Math.ceil(seconds));
    return `Wait ${whole} second${whole === 1 ? "" : "s"}`;
  }
  const minutes = Math.ceil(seconds / 60);
  return `Wait ${minutes} minute${minutes === 1 ? "" : "s"}`;
}

/**
 * The first message for `field` in a DRF validation error, in this repo's
 * shape `{error: {field: ["…"]}}` or bare `{field: ["…"]}`; e.g. the
 * checkout's `whatsapp_to` ("Enter a valid mobile number.").
 */
export function whatsappFieldError(
  error: unknown,
  field: string,
): string | null {
  const read = (value: unknown): string | null => {
    if (!value || typeof value !== "object") return null;
    const record = value as Record<string, unknown>;
    const nested = record.error;
    const fromNested =
      nested && typeof nested === "object"
        ? (nested as Record<string, unknown>)[field]
        : undefined;
    const messages = fromNested ?? record[field];
    if (Array.isArray(messages) && typeof messages[0] === "string") {
      return messages[0];
    }
    return typeof messages === "string" ? messages : null;
  };
  return (
    read(error) ?? read((error as { data?: unknown } | null)?.data ?? null)
  );
}

/**
 * What to tell a person about a refused WhatsApp request. A coded error gets
 * its plain words: the server's own sentence first for the `DETAIL_FIRST`
 * codes, then `overrides` (a code worded for one screen), then the shared
 * copy. Otherwise a DRF `{detail}`, `{error: "…"}`, the first field error or
 * `{message}` is used, else `fallback`.
 */
export function whatsappErrorMessage(
  error: unknown,
  fallback: string,
  overrides: Readonly<Record<string, string>> = {},
): string {
  const parts = whatsappErrorParts(error);
  if (parts) {
    const code = parts.code.toUpperCase();
    if (DETAIL_FIRST.has(code)) {
      return (
        plainWhatsappDetail(parts.detail) ??
        overrides[code] ??
        WHATSAPP_ERROR_COPY[code] ??
        fallback
      );
    }
    if (overrides[code]) return overrides[code];
    if (code === "OTP_THROTTLED" && parts.retryAfter) {
      return `Too many codes were asked for. ${whatsappRetryText(parts.retryAfter)}, then try again.`;
    }
    if (code === "THROTTLED" && parts.retryAfter) {
      return `Too many changes. ${whatsappTryAgainText(parts.retryAfter)}.`;
    }
    return (
      WHATSAPP_ERROR_COPY[code] ?? plainWhatsappDetail(parts.detail) ?? fallback
    );
  }
  if (!error || typeof error !== "object") return fallback;
  const body = error as Record<string, unknown>;
  if (typeof body.detail === "string" && body.detail.trim()) return body.detail;
  if (typeof body.error === "string" && body.error.trim()) return body.error;
  if (body.error && typeof body.error === "object") {
    const first = Object.values(body.error as Record<string, unknown>)[0];
    if (Array.isArray(first) && typeof first[0] === "string") return first[0];
    if (typeof first === "string") return first;
  }
  if (typeof body.message === "string" && body.message.trim()) {
    return body.message;
  }
  return fallback;
}
